import { useEffect, useState } from "react";
import { Smartphone, Upload, Trash2, Star, RefreshCw } from "lucide-react";
import AdminShell, { Panel, Btn } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api, apiBaseUrl, getErrorMessage } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

function fmtBytes(n) {
  const v = Number(n || 0);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(2)} MB`;
}

function CopyRow({ label, url, testid }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }
  if (!url) return null;
  return (
    <div className="mt-3">
      <p className="text-[11px] text-[#8892A8] font-semibold uppercase tracking-wide mb-1">{label}</p>
      <div className="flex items-center gap-2 bg-[#0A0F1F] border border-[#1E2845] rounded-lg px-3 py-2">
        <span className="text-[12px] text-[#E5E9F2] font-mono flex-1 break-all leading-snug" data-testid={testid}>
          {url}
        </span>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 px-2 py-1 rounded text-[11px] font-semibold transition"
          style={{ background: copied ? "rgba(52,211,153,0.15)" : "rgba(240,184,58,0.12)", color: copied ? "#34D399" : "#F0B83A" }}
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export default function AppReleases() {
  const [items, setItems] = useState([]);
  const [storageDir, setStorageDir] = useState("");
  const [liveRelease, setLiveRelease] = useState(null); // from /api/app-releases/latest

  const [version, setVersion] = useState("1.0.0");
  const [versionCode, setVersionCode] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [file, setFile] = useState(null);
  const [setAsLatest, setSetAsLatest] = useState(true);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const pager = useAdminPager();

  // Fetch /api/app-releases/latest so we get the real BACKEND_PUBLIC_URL-based
  // download_url — the exact same URL the /download page and QR code will use.
  async function fetchLiveRelease() {
    try {
      const { data } = await api.get("/api/app-releases/latest");
      setLiveRelease(data);
    } catch {
      setLiveRelease(null); // no published release yet
    }
  }

  async function load() {
    try {
      const { data } = await api.get("/api/admin/app-releases", {
        params: { skip: pager.skip, limit: pager.limit },
      });
      const { items, total, extra } = unwrapAdminList(data);
      setItems(items);
      pager.setTotal(total);
      setStorageDir(extra?.storage_dir || data.storage_dir || "");
      setErr("");
    } catch (e) {
      setErr(getErrorMessage(e));
    }
    await fetchLiveRelease();
  }

  useEffect(() => { load(); }, [pager.page]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) { setErr("Select an .apk file first."); return; }
    setBusy(true); setMsg(""); setErr(""); setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "coinmbx.apk");
      fd.append("version", version.trim());
      if (versionCode.trim()) fd.append("version_code", versionCode.trim());
      fd.append("release_notes", releaseNotes);
      fd.append("package_name", "com.coinmbx.app");
      fd.append("set_as_latest", setAsLatest ? "true" : "false");
      const { data } = await api.post("/api/admin/app-releases/upload", fd, {
        onUploadProgress: (ev) => {
          if (ev.total) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      setMsg(`✅ Uploaded v${data.release?.version} (${data.release?.release_id})`);
      setFile(null);
      setUploadPct(0);
      await load();
    } catch (ex) {
      setErr(getErrorMessage(ex));
      setUploadPct(0);
    } finally {
      setBusy(false);
    }
  }

  async function publish(id) {
    setBusy(true);
    try {
      await api.post(`/api/admin/app-releases/${id}/publish`);
      setMsg("Published as latest release.");
      await load();
    } catch (ex) {
      setErr(getErrorMessage(ex));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this release and remove the APK file from disk?")) return;
    setBusy(true);
    try {
      await api.delete(`/api/admin/app-releases/${id}`);
      setMsg("Release deleted.");
      await load();
    } catch (ex) {
      setErr(getErrorMessage(ex));
    } finally {
      setBusy(false);
    }
  }

  // ① Download landing page — best link to share with users.
  //    Always window.location.origin + /download (works in any env).
  const downloadPageUrl = `${window.location.origin}/download`;

  // ② Direct APK download URL — comes from BACKEND_PUBLIC_URL via /api/app-releases/latest.
  //    Falls back to proxy-based URL only if backend didn't return a full URL.
  const directApkUrl = liveRelease?.download_url
    || (liveRelease ? `${apiBaseUrl() || window.location.origin}/api/app-releases/latest/download` : null);

  const liveVersion = liveRelease?.version;

  return (
    <AdminShell
      title="Mobile App Releases"
      subtitle="Upload Android APK builds — stored on server disk, metadata in MongoDB"
    >
      {err && (
        <div className="mb-4 text-[13px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
          {err}
        </div>
      )}
      {msg && (
        <div className="mb-4 text-[13px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3">
          {msg}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ── Upload form ── */}
        <Panel className="p-5">
          <h3 className="text-[15px] font-bold text-[#E5E9F2] mb-4 flex items-center gap-2">
            <Upload size={16} className="text-[#F0B83A]" />
            Upload new APK
          </h3>
          <form onSubmit={handleUpload} className="space-y-4" data-testid="app-release-upload-form">
            <div>
              <label className="text-[12px] text-[#8892A8] font-semibold uppercase tracking-wide">
                APK file *
              </label>
              <input
                type="file"
                accept=".apk,application/vnd.android.package-archive"
                className="mt-2 block w-full text-[13px] text-[#E5E9F2]"
                onChange={(ev) => setFile(ev.target.files?.[0] || null)}
                data-testid="app-release-file"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] text-[#8892A8] font-semibold">Version *</label>
                <input
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-[#0A0F1F] border border-[#1E2845] text-[14px]"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="1.0.0"
                  required
                />
              </div>
              <div>
                <label className="text-[12px] text-[#8892A8] font-semibold">Version code</label>
                <input
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-[#0A0F1F] border border-[#1E2845] text-[14px]"
                  value={versionCode}
                  onChange={(e) => setVersionCode(e.target.value)}
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <label className="text-[12px] text-[#8892A8] font-semibold">Release notes</label>
              <textarea
                className="mt-1 w-full min-h-[80px] px-3 py-2 rounded-lg bg-[#0A0F1F] border border-[#1E2845] text-[14px]"
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                placeholder="Bug fixes, new features…"
              />
            </div>
            <label className="flex items-center gap-2 text-[13px] text-[#E5E9F2] cursor-pointer">
              <input
                type="checkbox"
                checked={setAsLatest}
                onChange={(e) => setSetAsLatest(e.target.checked)}
              />
              Publish immediately as latest (public download)
            </label>
            <Btn type="submit" disabled={busy}>
              {busy
                ? uploadPct > 0
                  ? `Uploading… ${uploadPct}%`
                  : "Uploading…"
                : "Upload APK"}
            </Btn>
            {busy && uploadPct > 0 && (
              <div className="w-full h-1.5 rounded-full bg-[#1E2845] overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#F0B83A] transition-all duration-200"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
            )}
          </form>
          {storageDir && (
            <p className="mt-4 text-[11px] text-[#8892A8] break-all">
              Server storage: {storageDir}
            </p>
          )}
        </Panel>

        {/* ── Shareable links panel ── */}
        <Panel className="p-5">
          <h3 className="text-[15px] font-bold text-[#E5E9F2] mb-1 flex items-center gap-2">
            <Smartphone size={16} className="text-[#F0B83A]" />
            Share with users
          </h3>

          {liveRelease ? (
            <>
              <p className="text-[13px] text-[#8892A8] mb-2">
                Live release:{" "}
                <span className="text-emerald-400 font-semibold">v{liveVersion}</span>
                {liveRelease.file_size > 0 && (
                  <span className="text-[#8892A8]"> · {fmtBytes(liveRelease.file_size)}</span>
                )}
              </p>

              {/* ① Download landing page — recommended */}
              <CopyRow
                label="① Share this page with users (recommended)"
                url={downloadPageUrl}
                testid="share-download-page-url"
              />
              <p className="text-[11px] text-[#8892A8] mt-1 mb-1 leading-snug">
                Opens the branded /download page with QR code, install steps &amp; version info.
              </p>

              {/* ② Direct APK URL */}
              <CopyRow
                label="② Direct APK download link"
                url={directApkUrl}
                testid="share-direct-apk-url"
              />
              <p className="text-[11px] text-[#8892A8] mt-1 leading-snug">
                Starts the APK download immediately — good for QR codes or deep links.
              </p>

              <a
                href={directApkUrl}
                className="mt-4 inline-flex text-[#F0B83A] text-[12px] font-semibold hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                Test direct APK download →
              </a>
            </>
          ) : (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[13px] text-amber-300">
              No published release yet. Upload an APK above and tick
              &ldquo;Publish immediately&rdquo; to generate shareable links.
            </div>
          )}

          <p className="mt-6 text-[11px] text-[#8892A8]">
            <code className="text-[#E5E9F2]">BACKEND_PUBLIC_URL</code> in backend .env controls
            the domain in the direct APK link. Currently:{" "}
            <code className="text-[#F0B83A] break-all">
              {directApkUrl
                ? (() => { try { return new URL(directApkUrl).origin; } catch { return directApkUrl; } })()
                : "not set"}
            </code>
          </p>
        </Panel>
      </div>

      {/* ── Release history ── */}
      <Panel className="mt-6 p-5">
        <h3 className="text-[15px] font-bold text-[#E5E9F2] mb-4 flex items-center gap-2">
          <RefreshCw size={16} className="text-[#F0B83A]" />
          Release history
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[#8892A8] border-b border-[#1E2845]">
                <th className="py-2 pr-4">Version</th>
                <th className="py-2 pr-4">Size</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Uploaded</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-[#8892A8]">
                    No releases yet. Upload an APK above.
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.release_id} className="border-b border-[#1E2845]/60">
                  <td className="py-3 pr-4 font-semibold text-[#E5E9F2]">
                    v{r.version}
                    {r.version_code != null && (
                      <span className="text-[#8892A8] font-normal"> ({r.version_code})</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-[#8892A8]">{fmtBytes(r.file_size)}</td>
                  <td className="py-3 pr-4">
                    {r.is_latest ? (
                      <span className="text-emerald-400 font-semibold">LIVE</span>
                    ) : r.is_published ? (
                      <span className="text-[#8892A8]">Published</span>
                    ) : (
                      <span className="text-[#8892A8]">Draft</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-[#8892A8]">
                    {r.uploaded_at ? new Date(r.uploaded_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-3">
                    <div className="flex flex-wrap gap-2">
                      {!r.is_latest && (
                        <button
                          type="button"
                          onClick={() => publish(r.release_id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#1E2845] hover:bg-[#2a3558] text-[#F0B83A]"
                        >
                          <Star size={12} /> Publish
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(r.release_id)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-500/20 text-red-300 hover:bg-red-500/30"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          testId="releases-pager"
        />
      </Panel>
    </AdminShell>
  );
}
